'use client';

import { PLACEHOLDER_IMAGE_SRC } from '@/lib/constants/assets';

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Minus, Plus, Trash2, Package, Heart } from 'lucide-react';

import { formatPrice } from '@/lib/utils/format';
import { useCart } from '@/providers/CartProvider';
import { useNotification } from '@/providers/NotificationProvider';
import Image from 'next/image';
import { CartItemPopulated } from '@/lib/Apis';
import { useProduct } from '@/hooks/useProducts';
import { useRouter } from 'next/navigation';
import { useBasePath } from '@/providers/BasePathProvider';


interface CartItemProps {
  item: CartItemPopulated;
}

export function CartItem({ item }: CartItemProps) {
  const { updateQuantity, removeFromCart, saveForLater } = useCart();
  const notification = useNotification();
  const router = useRouter();
  const { buildPath } = useBasePath();
  const [isRemoving, setIsRemoving] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fallback Logic:
  // If the backend doesn't populate 'product' on the cart item, we can try to find a productId
  // and fetch the product details to get the media.
  // We check various locations where productId might be hidden.
  const productId =
    (item as any).productId ||
    (item as any).product?._id ||
    (item as any).product?.id ||
    (item.productVariantData as any).productId ||
    (item.productVariantData as any).product?._id ||
    (item.productVariantData as any).product?.id;

  const { product } = useProduct(productId);

  const handleUpdateQuantity = (newQuantity: number) => {
    if (newQuantity < 1) return;
    updateQuantity(item.productVariantId, newQuantity);
  };

  const handleRemove = async () => {
    setIsRemoving(true);
    try {
      await removeFromCart(item.productVariantId);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleSaveForLater = async () => {
    setIsSaving(true);
    try {
      await saveForLater(item);
    } finally {
      setIsSaving(false);
    }
  };

  const itemTotal = item.productVariantData.finalPrice * item.quantity;
  const unitPrice = item.productVariantData.finalPrice;


  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      className="bg-white border-2 border-gray-100 rounded-[24px] p-4 md:p-6 hover:border-hsecondary/30 transition-all duration-300"
    >


      <div className="flex gap-3 md:gap-4">
        <div className="w-20 h-20 md:w-28 md:h-28 rounded-[16px] overflow-hidden bg-gray-50 shrink-0">
          <Image
            src={(item as any).product?.media?.[0]?.file || product?.media?.[0]?.file || item.productVariantData.media?.[0]?.file || PLACEHOLDER_IMAGE_SRC}
            alt={(item as any).product?.name || product?.name || item.productVariantData.name}
            className="w-full h-full object-cover"
            height={112}
            width={112}
          />
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex-1 min-w-0">
              <h3
                className="font-semibold text-[#2B4B7C] mb-0.5 cursor-pointer hover:underline"
                onClick={() => productId && router.push(buildPath(`/products/${productId}`))}
              >
                {(item as any).product?.name || product?.name || item.productVariantData.name}
              </h3>
              <p className="text-[12px] text-[#676c80] mb-2">{item.productVariantData.name}</p>
              <div className="flex items-center gap-2">
                  {item.productVariantData.inventoryCount === 0 ? (
                    <span className="text-[11px] text-red-500 font-medium">
                      Out of Stock
                    </span>
                  ) : item.productVariantData.inventoryCount <= 10 ? (
                    <span className="text-[11px] text-hprimary font-medium flex items-center gap-1">
                      <Package className="size-3" />
                      Only {item.productVariantData.inventoryCount} left in stock
                    </span>
                  ) : (
                    <span className="text-[11px] text-green-600 font-medium flex items-center gap-1">
                      <Package className="size-3" />
                      In Stock
                    </span>
                  )}
              </div>
            </div>
 
            {/* Action Buttons - Top Right */}
            <div className="flex items-center gap-1">
              <button
                onClick={handleSaveForLater}
                disabled={isSaving}
                className={`p-2 rounded-full transition-colors group ${isSaving ? 'bg-hprimary/10 cursor-not-allowed' : 'hover:bg-hprimary/10'}`}
                aria-label="Move to wishlist"
                title="Move to wishlist"
              >
                {isSaving ? (
                  <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-hprimary border-t-transparent" />
                ) : (
                  <Heart className="size-5 text-[#676c80] group-hover:text-hprimary group-hover:fill-hprimary/20 transition-colors" />
                )}
              </button>
              <button
                onClick={handleRemove}
                disabled={isRemoving}
                className={`p-2 rounded-full transition-colors group ${isRemoving ? 'bg-red-50 cursor-not-allowed' : 'hover:bg-red-50'}`}
                aria-label="Remove item"
                title="Remove item"
              >
                {isRemoving ? (
                  <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                ) : (
                  <Trash2 className="size-5 text-[#676c80] group-hover:text-red-500 transition-colors" />
                )}
              </button>
            </div>
          </div>

          {/* Quantity and Price */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-auto">
            {/* Quantity Controls */}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-3 rounded-full px-3 py-1.5 md:px-4 md:py-2 w-fit relative bg-gray-50">
                <button
                  onClick={() => handleUpdateQuantity(item.quantity - 1)}
                  disabled={item.quantity <= 1}
                  className="p-1 rounded-full transition-all hover:bg-white"
                  aria-label="Decrease quantity"
                >
                  <Minus className="size-3 md:size-4 text-[#2B4B7C]" />
                </button>
                <span className="font-semibold text-[13px] md:text-[14px] text-[#2B4B7C] min-w-[20px] text-center">
                  {item.quantity}
                </span>
                <button
                  onClick={() => {
                    const maxStock = item.productVariantData.inventoryCount || 999;
                    if (item.quantity >= maxStock) {
                      notification.error(
                        'Maximum Stock Reached',
                        `Sorry, only ${maxStock} units of this item are available.`
                      );
                      return;
                    }
                    handleUpdateQuantity(item.quantity + 1);
                  }}
                  className="p-1 rounded-full transition-all hover:bg-white"
                  aria-label="Increase quantity"
                >
                  <Plus className="size-3 md:size-4 text-[#2B4B7C]" />
                </button>
              </div>
              {item.quantity >= (item.productVariantData.inventoryCount || 999) && (
                <span className="text-[10px] text-hprimary font-medium px-2">
                  Maximum available reached
                </span>
              )}
            </div>

            {/* Price */}
            <div className="text-left sm:text-right">
              <p className="font-bold text-[16px] md:text-[18px] text-hsecondary">
                {formatPrice(itemTotal)}
              </p>
              <p className="text-[10px] md:text-[11px] text-[#676c80]">
                {formatPrice(unitPrice)} each
              </p>
            </div>
          </div>
        </div>

      </div>

    </motion.div>
  );
}

